import React, { useCallback, useEffect, useRef } from 'react';
import { LayoutChangeEvent, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useScrollToTop } from '@react-navigation/native';

import { AppNavBar } from '@/components/AppNavBar';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { StickyCellRendererComponent } from '@/feature/lobby/components/sticky-item/StickyCellRendererComponent';
import { StickyWrapper, useStickyScrollHandler } from '@/feature/lobby/hooks/use-sticky-translation';
import { LobbyProductSwitcherChipsHandle } from '@/feature/product-transition/components/LobbyProductSwitcherChipsHandle';
import {
    LobbyWrapper,
    productSwitcherStyles,
    useLobbyListStyles,
} from '@/feature/product-transition/components/LobbyWrapper';
import { PRODUCT_CHIPS_HEIGHT } from '@/feature/product-transition/components/ProductSwitcherChips';
import { LobbyCarousel } from '@/feature/promohub/components/LobbyCarousel';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { WhatsNewLobbyCard } from '@/feature/whats-new/components/WhatsNewLobbyCard';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { useResumeEffect } from '@/hooks/use-resume';
import { useWalletStore } from '@/hooks/use-wallet';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { RipsCategories } from '../components/RipsCategories';
import { RipsPackSections } from '../components/RipsPackSections';
import { RipsRipAgain } from '../components/RipsRipAgain';
import { useRecentPacks } from '../hooks/use-recent-packs';
import { RipsCategory, RipsPack, useRipsGames } from '../hooks/use-rips-games';
import { refreshRipsInventoryCount } from '../hooks/use-rips-inventory-count';
import { refreshExpiredRipsSession, useRipsSession } from '../hooks/use-rips-session';
import { useRipsTermsCheck } from '../hooks/use-rips-terms-check';

/**
 * Rips lobby. Rips has no native gameplay of its own — each card opens the Rips web app in a
 * fullscreen WebView, so the lobby is just the product switcher plus the list of entry points.
 */
export const RipsLobbyScreen = () => {
    // Warms up the Repackz session so the cards can open the web app straight away.
    const { state: sessionState, goToGame } = useRipsSession();
    const { data: games, isPending: gamesPending, refetch: fetchGames } = useRipsGames();
    const { data: recentPacks, isPending: recentPacksPending, refetch: fetchRecentPacks } = useRecentPacks();

    const loadWallet = useWalletStore(state => state.actions.loadUserWallet);
    const checkRipsTerms = useRipsTermsCheck();

    useResumeEffect(
        useCallback(() => {
            refreshExpiredRipsSession();
            fetchGames();
            fetchRecentPacks();
            refreshRipsInventoryCount();
            loadWallet();
        }, [fetchGames, fetchRecentPacks, loadWallet])
    );

    const isLoading = sessionState === 'loading' || (sessionState === 'ready' && gamesPending);
    const isError = sessionState === 'error' || !games || games.length === 0;

    const setLobbyReady = useWhatsNewStore(state => state.actions.setLobbyReady);
    useEffect(() => {
        if (!isLoading) {
            setLobbyReady(true);
        }
    }, [isLoading, setLobbyReady]);

    const appNavbarStickPosition = useSharedValue(0);
    const handleStickPosition = useSharedValue(0);
    const { onScroll, scrollDirection, scrollOffset, scrollVelocity } = useStickyScrollHandler();
    const { lobbyHandleHeight } = useLobbyListStyles();
    const insets = useSafeAreaInsets();
    const { height } = useWindowDimensions();

    const onNavbarLayout = useCallback(
        (event: LayoutChangeEvent) => {
            appNavbarStickPosition.value = event.nativeEvent.layout.y - insets.top;
        },
        [appNavbarStickPosition, insets.top]
    );

    const onHandleChipsLayout = useCallback(
        (event: LayoutChangeEvent) => {
            handleStickPosition.value = event.nativeEvent.layout.y + PRODUCT_CHIPS_HEIGHT + lobbyHandleHeight;
        },
        [handleStickPosition, lobbyHandleHeight]
    );

    const onCategoryPress = useCallback(
        async (category: RipsCategory) => {
            const hasAcceptedTerms = await checkRipsTerms();
            if (hasAcceptedTerms) {
                goToGame(category.gameId);
            }
        },
        [checkRipsTerms, goToGame]
    );

    const onPackPress = useCallback(
        async (pack: RipsPack) => {
            const hasAcceptedTerms = await checkRipsTerms();
            if (hasAcceptedTerms) {
                goToGame(pack.vendorId);
            }
        },
        [checkRipsTerms, goToGame]
    );

    const listRef = useRef<Animated.ScrollView>(null);
    //TODO: remove ts-ignore when upgrading react navigation to v7
    // @ts-ignore
    useScrollToTop(listRef);

    return (
        <LobbyWrapper>
            <Animated.ScrollView
                ref={listRef}
                onScroll={onScroll}
                showsVerticalScrollIndicator={showScrollIndicator}
                contentContainerStyle={[
                    productSwitcherStyles.lobbyListContentContainerStyle,
                    isWeb ? { minHeight: height + PRODUCT_CHIPS_HEIGHT } : styles.contentContainer,
                ]}
            >
                <Box zIndex={101} onLayout={onHandleChipsLayout}>
                    <LobbyProductSwitcherChipsHandle
                        handleStickPosition={handleStickPosition}
                        scrollDirection={scrollDirection}
                        scrollOffset={scrollOffset}
                        scrollVelocity={scrollVelocity}
                    />
                </Box>
                <StickyCellRendererComponent shouldBeSticky={true} style={styles.appNavBar} onLayout={onNavbarLayout}>
                    <StickyWrapper
                        scrollOffset={scrollOffset}
                        scrollVelocity={scrollVelocity}
                        initialPosition={appNavbarStickPosition}
                        scrollDirection={scrollDirection}
                    >
                        <Row width={'100%'} backgroundColor={ds.colorToken.background.neutral.base}>
                            <AppNavBar hideBetrBucks />
                        </Row>
                    </StickyWrapper>
                </StickyCellRendererComponent>
                <WhatsNewLobbyCard />
                <MaxWidthWrapper pt="s18">
                    {!isLoading && isError ? (
                        <>
                            <LobbyCarousel style={styles.lobbyCarousel} />
                            <Box px="s16">
                                <Text textAlign="center" variant="bodyMedium" color={ds.colorToken.text.secondary}>
                                    {'Rips is not available right now. Please try again later.'}
                                </Text>
                            </Box>
                        </>
                    ) : (
                        <>
                            <RipsCategories
                                categories={games ?? []}
                                isLoading={isLoading}
                                onCategoryPress={onCategoryPress}
                            />
                            <LobbyCarousel style={styles.lobbyCarousel} />
                            <RipsRipAgain
                                packs={recentPacks ?? []}
                                isLoading={isLoading || recentPacksPending}
                                onPackPress={onPackPress}
                            />
                            <RipsPackSections
                                categories={games ?? []}
                                isLoading={isLoading}
                                onCategoryPress={onCategoryPress}
                                onPackPress={onPackPress}
                            />
                        </>
                    )}
                </MaxWidthWrapper>
            </Animated.ScrollView>
        </LobbyWrapper>
    );
};

const styles = StyleSheet.create({
    appNavBar: {
        zIndex: 100,
        top: 0,
    },
    lobbyCarousel: {
        paddingBottom: 32,
    },
    contentContainer: {
        minHeight: '100%',
    },
});
